'use client';

/**
 * Magnifier-icon trigger that opens the full-width search overlay.
 *
 * Used when {@code header.searchPlacement === 'icon'}. Clicking opens
 * the LiveSearchBox in overlay mode (either catalog or suggest mode,
 * depending on whether {@code catalogSearch} is provided).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiveSearchBox } from './LiveSearchBox';
import type { SearchSectionConfig } from '../../portal';

export interface SearchIconTriggerClientProps {
  tenantSlug: string;
  inverse?: boolean;
  /** When set, opens LiveSearchBox in catalog mode. Source: shell.search. */
  catalogSearch?: SearchSectionConfig;
  /** Operator-authored placeholder for suggest mode. Source: header.searchPlaceholder. */
  placeholder?: string;
  /** Active locale — forwarded to LiveSearchBox for label resolution. */
  locale?: string;
  /** Fallback locale from shell.i18n.default. */
  localeFallback?: string;
  /** Numeric tenant ID from the host auth client. */
  tenantId?: string | number;
  /** Full path to the catalog search proxy route (includes app base path). */
  catalogApiPath?: string;
  /** Where Enter goes; forwarded verbatim to the dialog's LiveSearchBox. */
  resultsHref?: string;
}

export function SearchIconTriggerClient({
  tenantSlug,
  inverse = false,
  catalogSearch,
  placeholder,
  locale,
  localeFallback,
  tenantId,
  catalogApiPath,
  resultsHref,
}: SearchIconTriggerClientProps) {
  const { t } = useTranslation('site-builder-runtime');
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label={t('search.open')}
        onClick={() => setOpen(true)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: inverse ? 'rgba(255,255,255,0.1)' : 'var(--color-surface-page, #f7f8fa)',
          color: inverse ? 'rgba(255,255,255,0.85)' : 'var(--color-text-secondary, #5f6675)',
          border: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx={11} cy={11} r={8} />
          <line x1={21} y1={21} x2={16.65} y2={16.65} />
        </svg>
      </button>
      <LiveSearchBox
        resultsHref={resultsHref}
        tenantSlug={tenantSlug}
        variant="overlay"
        open={open}
        expandToDialog
        onClose={() => setOpen(false)}
        catalogSearch={catalogSearch}
        placeholder={placeholder}
        locale={locale}
        localeFallback={localeFallback}
        tenantId={tenantId}
        catalogApiPath={catalogApiPath}
      />
    </>
  );
}
